import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, CircularProgress, Tooltip, Chip } from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import WaveSurfer from "wavesurfer.js";

// Channel colors: guest (top, blue) and contact (bottom, green)
const GUEST_WAVE = "#90caf9";
const GUEST_PROGRESS = "#1976d2";
const CONTACT_WAVE = "#a5d6a7";
const CONTACT_PROGRESS = "#2e7d32";
const MONO_WAVE = "#b0bec5";
const MONO_PROGRESS = "#546e7a";

const formatTime = (seconds) => {
  const s = Math.floor(seconds || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};

const CHANNEL_HEIGHT = 44;

// Stacked-channel waveform player for a call recording. With a dual-channel
// (stereo) recording, the guest is the top waveform and the contact is the
// bottom one, sharing a single playhead. Mono recordings render as one waveform.
const CallRecordingPlayer = ({ url, dualChannel = true }) => {
  const containerRef = useRef(null);
  const wsRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!containerRef.current || !url) return;

    let destroyed = false;
    setReady(false);
    setError(false);
    setIsPlaying(false);
    setCurrentTime(0);

    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: CHANNEL_HEIGHT,
      waveColor: dualChannel ? GUEST_WAVE : MONO_WAVE,
      progressColor: dualChannel ? GUEST_PROGRESS : MONO_PROGRESS,
      cursorColor: "#37474f",
      cursorWidth: 1,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      splitChannels: dualChannel
        ? [
            { waveColor: GUEST_WAVE, progressColor: GUEST_PROGRESS },
            { waveColor: CONTACT_WAVE, progressColor: CONTACT_PROGRESS },
          ]
        : undefined,
      url,
    });
    wsRef.current = ws;

    ws.on("ready", () => {
      if (destroyed) return;
      setDuration(ws.getDuration());
      setReady(true);
    });
    ws.on("timeupdate", (t) => !destroyed && setCurrentTime(t));
    ws.on("play", () => !destroyed && setIsPlaying(true));
    ws.on("pause", () => !destroyed && setIsPlaying(false));
    ws.on("finish", () => !destroyed && setIsPlaying(false));
    ws.on("error", (err) => {
      console.error("Waveform load error:", err);
      if (!destroyed) setError(true);
    });

    return () => {
      destroyed = true;
      try {
        ws.destroy();
      } catch (e) {
        /* no-op: instance already torn down */
      }
      wsRef.current = null;
    };
  }, [url, dualChannel]);

  const togglePlay = () => {
    if (wsRef.current && ready) wsRef.current.playPause();
  };

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          Unable to load this recording. It may still be processing, or playback was blocked.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
      <Tooltip title={isPlaying ? "Pause" : "Play"}>
        <span>
          <IconButton color="primary" onClick={togglePlay} disabled={!ready}>
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
        </span>
      </Tooltip>

      {/* Per-channel labels aligned to the stacked waveforms */}
      {dualChannel && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: CHANNEL_HEIGHT * 2,
            py: 0.5,
          }}
        >
          <Chip label="Guest" size="small" sx={{ bgcolor: GUEST_WAVE, color: "#0d47a1" }} />
          <Chip label="Contact" size="small" sx={{ bgcolor: CONTACT_WAVE, color: "#1b5e20" }} />
        </Box>
      )}

      <Box sx={{ position: "relative", flex: 1, minWidth: 200 }}>
        <div ref={containerRef} />
        {!ready && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={18} />
            <Typography variant="caption" color="text.secondary">
              Loading waveform…
            </Typography>
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: "right" }}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </Typography>

      <Tooltip title="Download recording">
        <IconButton component="a" href={url} target="_blank" rel="noopener noreferrer" size="small">
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default CallRecordingPlayer;
